import '../../css/Tabs.css'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import type { SxProps, Theme } from '@mui/material'
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import type { NullableNumber } from '@shared_types/global_types'
import { isNonEmptyArray } from '@shared_utils/array_utils'
import clsx from 'clsx'
import { get, isFunction, isNumber, orderBy } from 'lodash'
import moment from 'moment-timezone'
import type { Dispatch, SetStateAction } from 'react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs'

import YisDangerButton from './YisDangerButton'
import YisPrimaryButton from './YisPrimaryButton'

const StyledTable = styled(Table, { shouldForwardProp: prop => prop !== 'isMecca' })<StyleProps>(({ isMecca }) => ({
  border: 'none',
  '& tr td': {
    paddingLeft: isMecca ? '8px !important' : '16px'
  }
}))

const HeaderTableRow = styled(TableRow)({
  backgroundColor: 'transparent',
  cursor: 'pointer'
})

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  border: 'none',
  borderBottom: '1px solid var(--table-border-color)',
  fontSize: `${theme.typography.body1.fontSize} !important`,
  fontWeight: 'bold !important',
  cursor: 'pointer'
}))

const HeaderContent = styled('div', { shouldForwardProp: prop => prop !== 'isActions' })<{ isActions: boolean }>(({ isActions }) => ({
  display: 'flex',
  justifyContent: isActions ? 'flex-end !important' : 'flex-start !important',
  alignItems: 'center'
}))

const StyledTableBody = styled(TableBody)({
  backgroundColor: 'transparent !important',
  border: 'none'
})

const BodyTableRow = styled(TableRow)({
  backgroundColor: 'transparent',
  cursor: 'default',
  '&:hover': {
    background: 'var(--secondary-bg) !important'
  }
})

const BodyTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  borderColor: 'var(--table-border-color)',
  fontSize: `${theme.typography.body1.fontSize} !important`,
  wordBreak: 'break-all',
  padding: '.3rem'
}))

const dateFormatSx: SxProps<Theme> = {
  wordBreak: 'normal',
  whiteSpace: 'nowrap',
  lineHeight: 0
}

const ActionsContent = styled('div')(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  borderColor: 'transparent',
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: `${theme.typography.body1.fontSize} !important`,
  wordBreak: 'break-all',
  padding: '.3rem'
}))

const ButtonMargin = styled('div')({
  marginRight: '1rem'
})

const PinContent = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  fontSize: '1.3rem',
  cursor: 'pointer',
  transition: '.2s'
})

const loadingContainerSx: SxProps<Theme> = {
  marginTop: '8rem'
}

export interface YisTableHeader<T> {
  name: string
  value: keyof T | string
  format?: string
  type?: 'date'
  width?: string | number
}

interface StyleProps {
  isMecca?: boolean
}

interface YisTableProps<T> {
  headers: YisTableHeader<T>[]
  body: T[]
  format?: string
  onClick?: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>, id: number) => void
  onDelete?: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>, id: number) => void
  onEdit?: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>, id: number) => void
  emptyMessage?: string
  loading?: boolean
  setState?: Dispatch<SetStateAction<T[]>>
  bodyCellStyle?: string
  headerCellStyle?: string
  sortBy?: string
  isMarginButton?: boolean
  className?: string
  sortable?: boolean
  bodyRowStyle?: string
  headerDiv?: string
  tableHeader?: string
  tableBody?: string
  bodyDiv?: string
  activeBody?: {
    colIndex: NullableNumber
    rowIndex: number
    class: string
  }
  toggleMessage?: (messageId: number) => void
  isMecca?: boolean
}

export default function YisTable<T> (p: YisTableProps<T>) {
  const {
    headers,
    body,
    className,
    format,
    onClick,
    onDelete,
    onEdit,
    emptyMessage = 'No data',
    loading,
    isMarginButton,
    setState,
    bodyCellStyle,
    headerCellStyle,
    sortBy,
    sortable = true,
    bodyRowStyle,
    headerDiv,
    tableHeader,
    tableBody,
    bodyDiv,
    activeBody,
    toggleMessage,
    isMecca,
    ...props
  } = p

  const { t } = useTranslation()
  const [sort, setSort] = useState('date')
  const [rows, setRows] = useState<T[]>([])

  useEffect(() => {
    setRows(body)
  }, [body])

  // set initial sorting either by given column or the 1st one
  useEffect(() => {
    setSort(sortBy || headers[0].name)
  }, [sortBy, headers])

  const setSortBy = (columnName: string) => {
    if (sortable) {
      const ordering: ['desc' | 'asc'] = columnName === sort ? ['desc'] : ['asc']
      const sortedValues = orderBy(rows, columnName, ordering)

      setRows(sortedValues)
      if (setState) {
        setState(sortedValues)
      }
      setSort(columnName === sort ? '' : columnName)
    }
  }

  const returnCell = (item: YisTableHeader<T>, obj: T) => {
    if (item.value === 'date' && !isMecca) {
      return moment(get(obj, 'date') as moment.Moment).format(format || 'dd/MM/yyyy')
    }

    if (item.value !== 'welcome') {
      // @ts-expect-error generic types error TBC
      return obj[item.value] || ''
    }

    if (isNumber(get(obj, 'welcome')) && get(obj, 'welcome') === 1) {
      return <PinContent>
        <BsPinAngleFill onClick={() => toggleMessage && toggleMessage(get(obj, 'id') as number)} />
      </PinContent>
    }

    return <PinContent title="Click to add this message to welcome page">
      <BsPinAngle onClick={() => toggleMessage && toggleMessage(get(obj, 'id') as number)} />
    </PinContent>
  }

  const getCellClassName = (colIndex: number, rowIndex: number) => {
    const parts = [bodyCellStyle]
    if (activeBody && activeBody.rowIndex === rowIndex && activeBody.colIndex === colIndex) {
      parts.push(activeBody.class)
    }
    return clsx(parts)
  }

  return (
    <>
      {isNonEmptyArray(rows) && <StyledTable
        className={clsx('admin-base-table', className)}
        isMecca={isMecca}
        {...props}
        stickyHeader
      >
        <TableHead className={tableHeader} style={{ backgroundColor: 'transparent' }}>
          <HeaderTableRow>
            {headers.map(header => <HeaderTableCell
              onClick={() => setSortBy(header.value as string)}
              key={String(header.value)}
              style={{ width: header.width || 'auto' }}
              className={headerCellStyle}
            >
              <HeaderContent
                isActions={header.value === 'actions'}
                className={headerDiv}
                style={{ justifyContent: 'center', alignItems: 'center' }}
              >
                {header.name}
                {header.value === 'actions' || !sortable ? null : <UnfoldMoreIcon />}
              </HeaderContent>
            </HeaderTableCell>)}
          </HeaderTableRow>
        </TableHead>

        <StyledTableBody className={clsx(tableBody, 'show-scroll')}>
          {rows.map((row, rowIndex) => <BodyTableRow
            data-name={get(row, 'date')}
            key={get(row, 'id', rowIndex) as number}
            className={bodyRowStyle}
          >
            {headers.map((header, index) => <BodyTableCell
              key={String(header.value)}
              style={{ width: header.width || 'auto' }}
              className={getCellClassName(index, rowIndex)}
              sx={header.value === 'date' ? dateFormatSx : undefined}
              onClick={e =>
                isFunction(onClick) && onClick(e as React.MouseEvent<HTMLDivElement>, get(row, 'id') as number)
              }
            >
              <div className={bodyDiv}>
                {header.value === 'actions' && (onEdit || onDelete) ? <ActionsContent>
                  {isFunction(onEdit) && (isMarginButton ? <ButtonMargin>
                    <YisPrimaryButton
                      buttonType="edit"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        onEdit(e, get(row, 'id', rowIndex) as number)
                      }
                    />
                  </ButtonMargin> : <div>
                    <YisPrimaryButton
                      buttonType="edit"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        onEdit(e, get(row, 'id', rowIndex) as number)
                      }
                    />
                  </div>)}

                  {isFunction(onDelete) && <div>
                    <YisDangerButton
                      dangerType="delete"
                      onClick={e => onDelete(e, get(row, 'id', rowIndex) as number)}
                    />
                  </div>}
                </ActionsContent> : (
                  returnCell(header, row)
                )}
              </div>
            </BodyTableCell>)}
          </BodyTableRow>)}
        </StyledTableBody>
      </StyledTable>}

      {!body.length && !loading && <Typography color="textPrimary" style={{ width: '100%' }} align="center" variant="subtitle1">
        {t(emptyMessage)}
      </Typography>}

      {loading === true && <Grid
        container
        sx={[{
          justifyContent: 'center',
          alignItems: 'center'
        }, ...(Array.isArray(loadingContainerSx) ? loadingContainerSx : [loadingContainerSx])]}>
        <CircularProgress />
      </Grid>}
    </>
  )
}
